/**
 * Turn any thrown value into a {@link NormalizedError}.
 *
 * Handles `Error` instances (and cross-realm / duplicated-module lookalikes),
 * strings, plain objects (`{message, name, stack}` or arbitrary JSON), and
 * everything else. Never throws.
 */

import type { NormalizedError } from "./types";

const MAX_CAUSES = 5;

function isErrorLike(value: unknown): value is { name?: unknown; message?: unknown; stack?: unknown; cause?: unknown } {
  return typeof value === "object" && value !== null && ("message" in value || "stack" in value || "name" in value);
}

function safeStringify(value: unknown): string {
  try {
    const json = JSON.stringify(value);
    return json === undefined ? String(value) : json;
  } catch {
    return Object.prototype.toString.call(value);
  }
}

function messageOf(value: unknown): string {
  if (typeof value === "string") return value;
  if (isErrorLike(value) && typeof value.message === "string") return value.message;
  if (value === undefined) return "undefined";
  if (value === null) return "null";
  if (typeof value === "object") return safeStringify(value);
  return String(value);
}

function collectCauses(value: unknown): string[] {
  const causes: string[] = [];
  let current: unknown = isErrorLike(value) ? value.cause : undefined;
  while (current !== undefined && current !== null && causes.length < MAX_CAUSES) {
    causes.push(messageOf(current));
    current = isErrorLike(current) ? current.cause : undefined;
  }
  return causes;
}

export function normalizeError(value: unknown): NormalizedError {
  try {
    const name = isErrorLike(value) && typeof value.name === "string" && value.name ? value.name : "Error";
    const message = messageOf(value);
    const stack = isErrorLike(value) && typeof value.stack === "string" && value.stack ? value.stack : undefined;
    const causes = collectCauses(value);
    return {
      name,
      message: message.length > 0 ? message : name,
      ...(stack ? { stack } : {}),
      ...(causes.length > 0 ? { causes } : {}),
    };
  } catch {
    return { name: "Error", message: "Unserializable error" };
  }
}
